import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { newsAPI, newsletterAPI } from '../services/api';

const News = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allNewsArticles, setAllNewsArticles] = useState<any[]>([]); // Tous les articles
  const [filteredNewsArticles, setFilteredNewsArticles] = useState<any[]>([]); // Articles filtrés pour News Grid
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  useEffect(() => {
    fetchAllNews();
    fetchCategories();
  }, [language]);

  useEffect(() => {
    // Filtrer les articles selon la catégorie sélectionnée pour la News Grid
    if (selectedCategory === 'all') {
      setFilteredNewsArticles(allNewsArticles);
    } else {
      setFilteredNewsArticles(allNewsArticles.filter(article => article.category === selectedCategory));
    }
  }, [selectedCategory, allNewsArticles]);

  const fetchAllNews = async () => {
    try {
      setLoading(true);
      // Récupérer TOUS les articles sans filtre
      const response = await newsAPI.getAll({ lang: language });
      if (response.records) {
        setAllNewsArticles(response.records);
        setFilteredNewsArticles(response.records); // Par défaut, afficher tous
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      // Fallback data is handled by the API service
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await newsAPI.getCategories();
      if (response.records) {
        const allCategories = [
          { category: 'all', name: t('news.categories.all'), count: allNewsArticles.length },
          ...response.records.map((cat: any) => ({
            category: cat.category,
            name: getCategoryName(cat.category),
            count: cat.count
          }))
        ];
        setCategories(allCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback categories
      setCategories([
        { category: 'all', name: t('news.categories.all'), count: allNewsArticles.length },
        { category: 'competitions', name: t('news.categories.competitions'), count: 0 },
        { category: 'athletes', name: t('news.categories.athletes'), count: 0 },
        { category: 'development', name: t('news.categories.development'), count: 0 },
        { category: 'partnerships', name: t('news.categories.partnerships'), count: 0 },
        { category: 'community', name: t('news.categories.community'), count: 0 },
        { category: 'environment', name: t('news.categories.environment'), count: 0 }
      ]);
    }
  };

  const getCategoryName = (categoryId: string) => {
    const categoryMap: {[key: string]: string} = {
      'competitions': t('news.categories.competitions'),
      'athletes': t('news.categories.athletes'),
      'development': t('news.categories.development'),
      'partnerships': t('news.categories.partnerships'),
      'community': t('news.categories.community'),
      'environment': t('news.categories.environment')
    };
    return categoryMap[categoryId] || categoryId;
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    try {
      setNewsletterLoading(true);
      await newsletterAPI.subscribe(newsletterEmail, language);
      setNewsletterMessage('Inscription réussie !');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterMessage('Erreur lors de l\'inscription');
    } finally {
      setNewsletterLoading(false);
      setTimeout(() => setNewsletterMessage(''), 3000);
    }
  };

  // Articles featured - TOUJOURS les mêmes, peu importe le filtre
  const featuredNews = allNewsArticles.filter(article => article.featured);


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des actualités...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-teal-800 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {t('news.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            {t('news.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Featured News - TOUJOURS affichée avec les articles featured */}
      {featuredNews.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('news.featured.title')}</h2>
              <p className="text-xl text-gray-600">{t('news.featured.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredNews.slice(0, 2).map((article) => (
                <Link 
                  key={article.id} 
                  to={`/news/${article.id}`}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {t('news.featured.title')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(article.created_at)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span>{article.author}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {article.tags && article.tags.split(',').slice(0, 2).map((tag: string, index: number) => (
                          <span key={index} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                            {tag.trim()}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium transition-colors">
                        <span>{t('news.readMore')}</span>
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

  

      {/* News Grid - Affiche les articles filtrés selon la catégorie */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {selectedCategory === 'all' ? 'Derniers articles' : getCategoryName(selectedCategory)}
                </h3>
              </div>

              {filteredNewsArticles.length > 0 ? (
                <div className="space-y-8">
                  {filteredNewsArticles.map((article) => (
                    <Link 
                      key={article.id} 
                      to={`/news/${article.id}`}
                      className="block bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3">
                        <div className="relative h-48 md:h-auto overflow-hidden">
                          <img 
                            src={article.image_url}
                            alt={article.title}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                          />
                         
                        </div>
                        
                        <div className="md:col-span-2 p-6">
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-4 w-4" />
                              <span>{formatDate(article.created_at)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <User className="h-4 w-4" />
                              <span>{article.author}</span>
                            </div>
                            <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs">
                              {getCategoryName(article.category)}
                            </span>
                          </div>
                          
                          <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                            {article.title}
                          </h3>
                          
                          <p className="text-gray-600 mb-4 leading-relaxed">
                            {article.excerpt}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-2">
                              {article.tags && article.tags.split(',').slice(0, 2).map((tag: string, index: number) => (
                                <span key={index} className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs">
                                  <Tag className="h-3 w-3 inline mr-1" />
                                  {tag.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">Aucun article trouvé pour cette catégorie.</p>
                  <button 
                    onClick={() => setSelectedCategory('all')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                  >
                    Voir toutes les actualités
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">


              {/* Newsletter Signup */}
              <div className="bg-gradient-to-br from-blue-600 to-teal-600 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4">{t('news.newsletter.title')}</h3>
                <p className="text-blue-100 mb-6">
                  {t('news.newsletter.subtitle')}
                </p>
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <input 
                    type="email" 
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder={t('news.newsletter.placeholder')}
                    className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                    required
                  />
                  <button 
                    type="submit"
                    disabled={newsletterLoading}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                  >
                    {newsletterLoading ? 'Inscription...' : t('news.newsletter.subscribe')}
                  </button>
                  {newsletterMessage && (
                    <p className="text-sm text-center">{newsletterMessage}</p>
                  )}
                </form>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('news.social.title')}</h3>
                <div className="space-y-3">
                  <a href="#" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold">IG</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Instagram</div>
                      <div className="text-sm text-gray-600">@ascsurf_official</div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
                  </a>
                  
                  <a href="#" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold">YT</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">YouTube</div>
                      <div className="text-sm text-gray-600">ASCsurf Official</div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-gray-400 ml-auto" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;